import { fmt, type CalcResult, type H2State, type Scenario } from "@h2lca/core";
import { SegmentedControl, TextField } from "@/components/ui/FormControls";
import { methodById, useMaster } from "@/store/master";
import { useWorkshop } from "@/store/workshop";
import { GlossaryTip } from "../GlossaryTip";
import { DENOMINATOR_LABEL, H2_STATE_LABEL } from "../helpers";
import { NumField } from "../NumField";
import { Block, Section } from "../Section";

/** ② 기능단위,기준흐름 */
export function FuStep({ scenario, result }: { scenario: Scenario; result: CalcResult }) {
  const data = useMaster((s) => s.data);
  const update = useWorkshop((s) => s.update);
  const m = methodById(data, scenario.method);
  const locked = m.functionalUnit.locked;
  const fu = scenario.functionalUnit;
  const setFu = (patch: Partial<Scenario["functionalUnit"]>) => update((s) => ({ ...s, functionalUnit: { ...s.functionalUnit, ...patch } }));

  const denomKg = result.denominator.kg;
  const produced = result.batch.producedKg;
  const refFlow = denomKg > 0 ? (produced / denomKg) * result.functionalUnitKg : null;
  const purityMin = m.functionalUnit.purityMinPct;
  const purityBad = m.id === "cert" && purityMin !== undefined && fu.purityPct < purityMin;

  return (
    <Section
      title={
        <>
          기능단위와 기준흐름 <GlossaryTip k="functional_unit" />
        </>
      }
      desc="결과를 나타내는 기준(기능단위)과, 그 기준을 채우기 위해 생산해야 하는 양(기준흐름)을 정합니다."
    >
      {locked && <p className="text-footnote text-ink-subtle">인증제 모의산정은 기능단위를 수소 {m.functionalUnit.h2Kg} kg으로 고정합니다. 순도, 압력, 상태는 참고로 적되, 순도는 적용조건({purityMin}% 이상)을 확인합니다.</p>}
      <div className="ws-form-grid">
        <NumField label="기능단위 수소량" unit="kg" value={fu.h2Kg} allowNull={false} min={0} disabled={locked} onChange={(v) => v !== null && v > 0 && setFu({ h2Kg: v })} hint={locked ? "인증제 조건으로 고정" : "보통 1 kg"} />
        <NumField label="순도" unit="%" value={fu.purityPct} allowNull={false} min={0} onChange={(v) => v !== null && setFu({ purityPct: v })} hint={purityMin !== undefined ? `인증제 적용조건 ${purityMin}% 이상` : "예: 99.97"} />
        <NumField label="압력" unit="bar" value={fu.pressureBar} allowNull={false} min={0} onChange={(v) => v !== null && setFu({ pressureBar: v })} />
        <div className="control-field">
          <p className="control-label">상태</p>
          <SegmentedControl<H2State>
            label="수소 상태"
            value={fu.state}
            options={[
              { value: "gas", label: H2_STATE_LABEL.gas },
              { value: "liquid", label: H2_STATE_LABEL.liquid },
            ]}
            onChange={(v) => setFu({ state: v })}
            disabled={locked && m.functionalUnit.state !== undefined}
          />
        </div>
      </div>
      <TextField label="인도지점" value={fu.deliveryPoint} onChange={(e) => setFu({ deliveryPoint: e.target.value })} disabled={locked && m.functionalUnit.deliveryPoint !== undefined} hint="예: 수소충전소 저장탱크 입고, 생산시설 출하지점" />

      {purityBad && <p className="note note-danger text-caption">인증제 모의산정 조건 미충족: 수소 순도 {fu.purityPct}%가 적용조건({purityMin}% 이상)에 못 미칩니다. 등급을 판정하지 않습니다.</p>}

      <Block
        title={
          <>
            기준흐름 <GlossaryTip k="reference_flow" />
          </>
        }
      >
        <p className="text-caption text-ink-soft">
          기능단위: <b className="text-ink">{fu.purityPct}%, {fu.pressureBar} bar, {H2_STATE_LABEL[fu.state]}</b> 상태로 <b className="text-ink">{fu.deliveryPoint}</b>에 인도되는 수소 <b className="font-mono text-ink">{result.functionalUnitKg} kg</b>
        </p>
        <dl className="ws-kv">
          <dt>생산량(경계 안)</dt>
          <dd>{fmt(produced)} kg</dd>
          <dt>{DENOMINATOR_LABEL[result.denominator.kind]}</dt>
          <dd>{fmt(denomKg)} kg</dd>
          <dt>손실 합계</dt>
          <dd>{fmt(result.batch.lossKg)} kg</dd>
          <dt>기준흐름</dt>
          <dd>
            {refFlow === null ? (
              "설비를 구성하면 계산됩니다"
            ) : (
              <>
                {fmt(produced)} ÷ {fmt(denomKg)} × {result.functionalUnitKg} = <b>{fmt(refFlow, 4)} kg</b>
              </>
            )}
          </dd>
        </dl>
        <p className="text-footnote text-ink-subtle">인도 수소 {result.functionalUnitKg} kg 을 위해 생산시설이 만들어야 하는 수소량입니다. 손실이 있을수록 커집니다.</p>
      </Block>
    </Section>
  );
}
